import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mic, Wand2, Send, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "../site/HeroSlides";
import { VoicePick, VoiceSample } from "../invites/InviteExtras";

function Composer({ n, setN, voices, err }) {
  const [text, setText] = useState(n.text || "");
  const [vid, setVid] = useState(n.voice_id || "");
  useEffect(() => { if (!vid && voices?.[0]) setVid(voices[0].voice_id); }, [voices]); // eslint-disable-line
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");
  async function run(kind, fn) { setBusy(kind); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); } }
  const draft = () => run("draft", async () => { const { data } = await api.post("/weddings/mine/voice-thanks/draft", { notes }); setText(data.text); });
  const record = () => run("rec", async () => {
    if (text.trim().length < 10) throw new Error("Write a few words first");
    const { data } = await api.post("/weddings/mine/voice-thanks/record", { voice_id: vid, text });
    setN({ ...n, ...data, sent: 0 }); toast.success("Your thank-you is recorded");
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={300} placeholder="Anything to mention? e.g. the dancing, the rain" className="input-luxe flex-1 min-w-[220px] text-sm" data-testid="vthx-notes" />
        <button onClick={draft} disabled={!!busy} className="btn-outline-gold text-xs" data-testid="vthx-draft">{busy === "draft" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Write it for me</button>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={800} rows={5} placeholder="Thank you so much for celebrating with us…" className="input-luxe text-sm" data-testid="vthx-text" />
      <p className="text-[11px] text-[var(--muted)] text-right">{text.length}/800</p>
      {err ? <p className="text-xs text-[var(--muted)]" data-testid="vthx-voice-off"><Mic className="w-3 h-3 inline" /> {err}</p> : !voices ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
        <VoicePick voices={voices} vid={vid} setVid={setVid} onGo={record} busy={busy === "rec"} label={n.audio_path ? "Record again" : "Record in this voice"} testid="vthx-voice" />
        <VoiceSample vid={vid} saved={!!n.audio_path} what="thank-you" request={() => api.post("/weddings/mine/voice-thanks/sample", { voice_id: vid, text: text.trim() || "Thank you so much for celebrating with us." }, { responseType: "blob" })} />
      </>}
    </div>
  );
}

function Sender({ n, setN }) {
  const [opt, setOpt] = useState({ email: true, text: true });
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!window.confirm(`Send your thank-you to ${n.attending} guest${n.attending === 1 ? "" : "s"} who said yes?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/weddings/mine/voice-thanks/send", opt);
      setN((await api.get("/weddings/mine/voice-thanks")).data);
      toast.success(`Sent to ${data.sent + data.mocked} guest${data.sent + data.mocked === 1 ? "" : "s"}${data.mocked ? ` (${data.mocked} waiting on email/text setup)` : ""}${data.skipped ? ` · ${data.skipped} already had it` : ""}${data.no_contact ? ` · ${data.no_contact} ${data.no_contact === 1 ? "has" : "have"} no way to reach by the picked option (they'll see it in their guest portal)` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div className="rounded-xl bg-[var(--bg-warm)] p-4 space-y-3" data-testid="vthx-send-box">
      <audio src={fileUrl(n.audio_path)} controls className="w-full" data-testid="vthx-audio" />
      {n.text && <p className="text-sm italic text-[var(--muted)] whitespace-pre-line" data-testid="vthx-recorded-text">"{n.text}"</p>}
      <p className="text-sm" data-testid="vthx-status">{n.attending} guest{n.attending === 1 ? "" : "s"} RSVP'd yes · {n.sent} sent so far. It also shows in their guest portal.</p>
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={opt.email} onChange={(e) => setOpt({ ...opt, email: e.target.checked })} data-testid="vthx-opt-email" /> Email</label>
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={opt.text} onChange={(e) => setOpt({ ...opt, text: e.target.checked })} data-testid="vthx-opt-text" /> Text message</label>
        <button onClick={go} disabled={busy || !n.attending} className="btn-gold text-xs" data-testid="vthx-send">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send to guests</button>
      </div>
    </div>
  );
}

export function VoiceThanks() {
  const [n, setN] = useState(null);
  const [voices, setVoices] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/weddings/mine/voice-thanks").then((r) => setN(r.data)).catch(() => {});
    api.get("/voice/voices").then((r) => setVoices(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);
  if (!n) return null;
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5 mb-6 space-y-4" data-testid="voice-thanks">
      <div><h3 className="font-serif text-2xl flex items-center gap-2"><Mic className="w-5 h-5 text-[var(--gold)]" /> Voice thank-you</h3>
        <p className="text-xs text-[var(--muted)]">Write one heartfelt message, hear it in a voice you love, then send it to everyone who came.</p></div>
      <Composer n={n} setN={setN} voices={voices} err={err} />
      {n.audio_path && <Sender n={n} setN={setN} />}
    </div>
  );
}
